/**
 * STEP 5: PHOTOS
 * 
 * Photo capture, management, and fixture linkage
 */

import React, { useState } from 'react';
import { Camera, Filter } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { PHOTO_CATEGORIES } from '../constants/photoCategories';
import {
  PhotoCapture,
  PhotoGrid,
  PhotoEditor,
  MissingPhotosWarning,
} from './photos';

const Step5Photos = ({ 
  data, 
  areas,
  onAddPhoto, 
  onRemovePhoto,
  validationErrors = [],
  disabled = false,
  onSave,
  onPrev,
  onNext,
  saving,
  isComplete,
}) => {
  // Wrapper for updating data
  const updateData = (newData) => {
    // Photos use addPhoto/removePhoto handlers directly
  };
  
  const measurementAreas = areas || [];
  const [filterArea, setFilterArea] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [editingPhotoId, setEditingPhotoId] = useState(null);
  
  // Initialize data structure if needed
  const photos = data || {
    items: [],
  };
  
  // Use measurementAreas from props

  const handleCapture = async (file) => {
    // In a real app, you'd upload this file to storage
    // For now, create a local URL
    const fileUrl = URL.createObjectURL(file);
    
    const newPhoto = {
      photo_id: uuidv4(),
      file_id: null,
      file_url: fileUrl,
      thumbnail_url: fileUrl,
      category: 'existing',
      caption: '',
      area_id: null,
      fixture_key: null,
      created_at: new Date().toISOString(),
    };
    
    updateData({
      ...photos,
      items: [...photos.items, newPhoto],
    });
    
    // Open editor for the new photo
    setEditingPhotoId(newPhoto.photo_id);
  };

  const handleUpload = async (files) => {
    const newPhotos = files.map(file => ({
      photo_id: uuidv4(),
      file_id: null,
      file_url: URL.createObjectURL(file),
      thumbnail_url: URL.createObjectURL(file),
      category: 'existing',
      caption: '',
      area_id: null,
      fixture_key: null,
      created_at: new Date().toISOString(),
    }));
    
    updateData({
      ...photos,
      items: [...photos.items, ...newPhotos],
    });
    
    // Open editor for the first new photo
    if (newPhotos.length > 0) {
      setEditingPhotoId(newPhotos[0].photo_id);
    }
  };

  const handleEditPhoto = (photoId) => {
    setEditingPhotoId(photoId);
  };

  const handleSavePhoto = (editedPhoto) => {
    updateData({
      ...photos,
      items: photos.items.map(p => 
        p.photo_id === editedPhoto.photo_id ? editedPhoto : p
      ),
    });
  };

  const handleRemovePhoto = (photoId) => {
    if (!window.confirm('Are you sure you want to remove this photo?')) return;
    
    updateData({
      ...photos,
      items: photos.items.filter(p => p.photo_id !== photoId),
    });
  };

  const handleAddPhotoForFixture = (item) => {
    // This would ideally open the camera with pre-filled linkage
    // For now, we'll just alert the user
    alert(`Please capture a photo for ${item.fixture_label} in ${item.area_label}`);
  };

  // Get the photo being edited
  const editingPhoto = editingPhotoId 
    ? photos.items.find(p => p.photo_id === editingPhotoId)
    : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Camera className="w-6 h-6 text-emerald-400" />
            <h2 className="text-xl font-bold text-slate-100">Photos</h2>
          </div>
          <p className="text-sm text-slate-400 mt-1">
            Document existing conditions and link to fixtures
          </p>
        </div>
        
        {/* Photo count */}
        <div className="text-center">
          <div className="text-2xl font-bold text-slate-200">{photos.items.length}</div>
          <div className="text-xs text-slate-500">Photos</div>
        </div>
      </div>

      {/* Capture/Upload Buttons */}
      <PhotoCapture
        onCapture={handleCapture}
        onUpload={handleUpload}
        disabled={disabled}
      />

      {/* Missing Photos Warning */}
      {measurementAreas.length > 0 && (
        <MissingPhotosWarning
          photos={photos.items}
          areas={measurementAreas}
          onAddPhoto={handleAddPhotoForFixture}
        />
      )}

      {/* Filters */}
      {photos.items.length > 0 && (
        <div className="flex items-center gap-4 p-3 bg-slate-800/50 border border-slate-700 rounded">
          <Filter className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-400">Filter:</span>
          
          <select
            value={filterArea}
            onChange={(e) => setFilterArea(e.target.value)}
            className="px-2 py-1 bg-slate-900 border border-slate-700 text-slate-100 text-sm rounded-sm
              focus:border-emerald-500 focus:outline-none"
          >
            <option value="">All Areas</option>
            {measurementAreas.map(area => (
              <option key={area.area_id} value={area.area_id}>
                {area.label}
              </option>
            ))}
          </select>
          
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-2 py-1 bg-slate-900 border border-slate-700 text-slate-100 text-sm rounded-sm
              focus:border-emerald-500 focus:outline-none"
          >
            <option value="">All Categories</option>
            {PHOTO_CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
          
          {(filterArea || filterCategory) && (
            <button
              onClick={() => { setFilterArea(''); setFilterCategory(''); }}
              className="text-xs text-emerald-400 hover:text-emerald-300"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Photo Grid */}
      {photos.items.length > 0 ? (
        <PhotoGrid
          photos={photos.items}
          areas={measurementAreas}
          filterArea={filterArea}
          filterCategory={filterCategory}
          onEditPhoto={handleEditPhoto}
          onRemovePhoto={handleRemovePhoto}
          disabled={disabled}
        />
      ) : (
        <div className="py-12 text-center border-2 border-dashed border-slate-700 rounded-lg">
          <Camera className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <h4 className="text-slate-400 mb-2">No photos yet</h4>
          <p className="text-sm text-slate-500 mb-4">
            Capture or upload photos to document existing conditions
          </p>
        </div>
      )}

      {/* Photo Editor Modal */}
      <PhotoEditor
        photo={editingPhoto}
        areas={measurementAreas}
        isOpen={!!editingPhotoId}
        onClose={() => setEditingPhotoId(null)}
        onSave={handleSavePhoto}
      />
    </div>
  );
};

export default Step5Photos;
